import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, Bot, User, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { getOpenAIInstance } from '../../lib/openai';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export function CustomerSupportAssistant() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatHistoryRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Debounce scroll events for better performance
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const chatHistory = chatHistoryRef.current;

    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // Add infinite scroll logic here if needed
      }, 150);
    };

    chatHistory?.addEventListener('scroll', handleScroll);
    return () => {
      chatHistory?.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const openai = await getOpenAIInstance();
      const completion = await openai.chat.completions.create({
        messages: [
          {
            role: 'system',
            content:
              'You are a helpful customer support assistant for an accounting software platform. Provide clear, concise, and accurate responses to user inquiries about accounting, taxes, and the software features.',
          },
          ...messages.map(msg => ({
            role: msg.role,
            content: msg.content,
          })),
          {
            role: 'user',
            content: input.trim(),
          },
        ],
        model: 'gpt-4-turbo-preview',
      });

      const assistantMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: completion.choices[0].message.content || '',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error getting response:', error);
      // Show error message to user
      setMessages(prev => [
        ...prev,
        {
          id: Date.now().toString(),
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="bg-dark-card rounded-lg shadow p-6 h-[600px] flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-6 w-6 text-primary-400" />
          <h2 className="text-xl font-semibold text-dark-text">Support Assistant</h2>
        </div>
      </div>

      <div
        ref={chatHistoryRef}
        className="flex-1 overflow-y-auto mb-4 space-y-4 scrollbar-thin scrollbar-thumb-dark-border scrollbar-track-dark-bg"
      >
        {messages.length === 0 && (
          <div className="text-center text-dark-muted py-8">
            <Bot className="h-12 w-12 mx-auto mb-4 text-primary-400" />
            <p>Hi! I'm your AI assistant. How can I help you today?</p>
          </div>
        )}

        {messages.map((message, index) => (
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`flex items-start space-x-2 ${
              message.role === 'assistant' ? 'justify-start' : 'justify-end'
            }`}
          >
            {message.role === 'assistant' && (
              <div className="w-8 h-8 rounded-full bg-primary-400/10 flex items-center justify-center flex-shrink-0">
                <Bot className="h-5 w-5 text-primary-400" />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message.role === 'assistant'
                  ? 'bg-dark-bg text-dark-text'
                  : 'bg-primary-600 text-dark-text'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
              <p className="text-xs mt-1 opacity-60">
                {new Date(message.timestamp).toLocaleTimeString()}
              </p>
            </div>
            {message.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-primary-600/10 flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-primary-400" />
              </div>
            )}
          </motion.div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="relative">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question... (Press Enter to send)"
          className="w-full bg-dark-bg text-dark-text rounded-lg pl-4 pr-12 py-3 focus:outline-none focus:ring-2 focus:ring-primary-400"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-primary-400 hover:text-primary-300 transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Send className="h-6 w-6" />}
        </button>
      </form>
    </div>
  );
}
